import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Avatar, Box, Tooltip } from "@mui/material";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch";
import useDownload from "../../hooks/useDownload";
import IconButton from "../../components/UI/IconButton";
import { OverflowTooltip } from "../../components/UI/TooltipOverflow";
import { IRefundMediaFile } from "../../models/refunds";
import RefundsService from "../../services/refunds.service";

const translationPath = "components.fileItem.";

const DOWNLOAD_ROUTE = process.env.REACT_APP_FILEMANAGER + "/api/v1/Download";

const refundsService = new RefundsService();

interface IRefundAttachmentItemProps {
  file: IRefundMediaFile;
}

const removeExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, "");

const RefundAttachmentItem = ({ file }: IRefundAttachmentItemProps) => {
  const { t } = useTranslation();

  const [isOpening, setIsOpening] = useState(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  const { sendRequest: getSignedUrl } = useFetch(
    refundsService.getMediaFileSignedUrl
  );

  const { downloadFileParams, loading: isDownloadingFile } = useDownload();

  const displayName = removeExtension(file.fileName);

  const handleOpen = useCallback(async () => {
    setIsOpening(true);
    // Abre a aba antes do await para não ser bloqueada como popup
    const newTab = window.open("", "_blank");

    const { data, success } = await getSignedUrl(file.id);

    if (!success || !data?.url) {
      newTab?.close();
      setIsOpening(false);
      return;
    }

    if (newTab) {
      newTab.location.href = data.url;
    } else {
      window.open(data.url, "_blank");
    }
    setIsOpening(false);
  }, [getSignedUrl, file.id]);

  const handleDownload = useCallback(async () => {
    setIsFetchingUrl(true);

    const { data, success } = await getSignedUrl(file.id);

    setIsFetchingUrl(false);

    if (!success || !data?.url) return;

    // O FileManager baixa do S3 pela URL assinada e devolve os bytes,
    // evitando o bloqueio de CORS do CDN no navegador
    downloadFileParams(
      DOWNLOAD_ROUTE,
      { SignedUrl: data.url },
      data.fileName || file.fileName
    );
  }, [getSignedUrl, downloadFileParams, file.id, file.fileName]);

  const isDownloading = isFetchingUrl || isDownloadingFile;
  const loading = isOpening || isDownloading;

  return (
    <Box
      sx={(t) => ({
        p: 1,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        borderBottom: 1,
        borderBottomColor: t.palette.divider,
        transition: "ease-in-out 0.1s",
        "&:hover": {
          backgroundColor:
            t.palette.mode === "light" ? t.palette.grey[100] : t.palette.grey[900],
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Avatar
          sx={(t) => ({
            width: 28,
            height: 28,
            bgcolor: t.palette.primary.main,
          })}
        >
          <AttachFileIcon sx={{ fontSize: 16 }} />
        </Avatar>
        <OverflowTooltip
          text={displayName}
          tooltip={file.fileName}
          textTransform="none"
          variant="subtitle2"
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Tooltip title={t(translationPath + "open_new_tab")} placement="left">
          <Box>
            <IconButton
              size="small"
              loading={isOpening}
              disabled={loading}
              onClick={handleOpen}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
        <Tooltip title={t(translationPath + "download_file")} placement="right">
          <Box>
            <IconButton
              size="small"
              loading={isDownloading}
              disabled={loading}
              onClick={handleDownload}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default memo(RefundAttachmentItem);
